// ── DASHBOARD PAGE (fp-nofodesk-9) — extracted from App.js ──
import { useState, useRef, useEffect, useMemo } from "react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { FileText } from "lucide-react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT, OP } from "../theme";
import { useWindowWidth } from "../hooks/useWindowWidth";
import { GatedButton } from "../components/PlanGate";
import { exportDashboardPPT } from "../utils/exportPPT";
import { apiLoadTable, getToken } from "../utils/api";
import { loadOrgProfile, parseAmt, fmtUSDFull } from "../utils/grants";

// ── Dashboard sub-components — defined at module scope so React never
//    unmounts/remounts them on parent re-renders ─────────────────────

function DashChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: OP.tooltipBg, border: `1px solid ${OP.tooltipBorder}`, borderRadius: 6, padding: "10px 14px", fontSize: 12, fontFamily: FONT_UI }}>
      <div style={{ color: TOKENS.textSecondary, fontWeight: 600, marginBottom: 4 }}>FY {label}</div>
      <div style={{ color: TOKENS.gold, fontVariantNumeric: "tabular-nums" }}>{fmtUSDFull(payload[0].value)}</div>
    </div>
  );
}

function DashPieTooltip({ active, payload, pieData }) {
  if (!active || !payload?.length) return null;
  const total = (pieData || []).reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ background: OP.tooltipBg, border: `1px solid ${OP.tooltipBorder}`, borderRadius: 6, padding: "9px 13px", fontSize: 12, fontFamily: FONT_UI }}>
      <div style={{ color: payload[0].payload.color, fontWeight: 600, marginBottom: 3 }}>{payload[0].name}</div>
      <div style={{ color: TOKENS.textSecondary }}>{payload[0].value} grants ({total > 0 ? Math.round(payload[0].value / total * 100) : 0}%)</div>
    </div>
  );
}

function DashBar3D(props) {
  const { x, y, width, height, fill } = props;
  if (!height || height <= 0) return null;
  const depth = Math.min(width * 0.35, 10);
  return (
    <g>
      <defs>
        <linearGradient id={`bar3d-front-${x}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fill} stopOpacity={1} />
          <stop offset="100%" stopColor={fill} stopOpacity={0.65} />
        </linearGradient>
      </defs>
      <rect x={x} y={y} width={width} height={height} fill={`url(#bar3d-front-${x})`} rx={2} />
      <polygon points={`${x},${y} ${x + width},${y} ${x + width + depth},${y - depth} ${x + depth},${y - depth}`} fill={fill} opacity={0.9} />
      <polygon points={`${x + width},${y} ${x + width + depth},${y - depth} ${x + width + depth},${y + height - depth} ${x + width},${y + height}`} fill={fill} opacity={0.45} />
    </g>
  );
}

function DashKPICard({ label, rawValue, formatFn, subtitle, status, onClick, isMobile }) {
  const T = useT();
  const [hov, setHov] = useState(false);
  const reduced = useRef(window.matchMedia("(prefers-reduced-motion: reduce)").matches).current;
  const [display, setDisplay] = useState(reduced ? rawValue : 0);

  useEffect(() => {
    if (reduced) { setDisplay(rawValue); return; }
    const duration = 600;
    const t0 = performance.now();
    const tick = (t) => {
      const p  = Math.min((t - t0) / duration, 1);
      const ep = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(rawValue * ep));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [rawValue, reduced]);

  const ACCENT = { green: TOKENS.statusGreen, amber: TOKENS.statusAmber, red: TOKENS.statusRed, blue: TOKENS.statusBlue };
  const accent = ACCENT[status] || TOKENS.statusGreen;

  return (
    <div
      onClick={onClick || undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov && onClick ? T.bgElevated : T.bgSurface,
        border: `1px solid ${T.border}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: TOKENS.radiusMd,
        padding: TOKENS.space6,
        cursor: onClick ? "pointer" : "default",
        transition: "background 0.15s",
        userSelect: "none",
        boxShadow: `0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}
    >
      <div style={{ fontSize: 10, fontFamily: FONT_UI, color: T.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
        {label}
      </div>
      <div style={{ fontSize: isMobile ? TOKENS.text2xl : TOKENS.text3xl, fontFamily: FONT_MONO, fontWeight: 500, color: T.text, lineHeight: 1.05, fontVariantNumeric: "tabular-nums", marginBottom: 10 }}>
        {formatFn(display)}
      </div>
      <div style={{ fontSize: 12, fontFamily: FONT_UI, color: T.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent, flexShrink: 0, boxShadow: `0 0 6px ${accent}` }} />
        {subtitle}
      </div>
    </div>
  );
}

function GettingStartedWidget({ activeCount, appsLen, setActiveView, setGosSubTab, T }) {
  const orgProfile = loadOrgProfile();
  const hasOrg = !!(orgProfile?.name || orgProfile?.orgName);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('nofodesk_gs_dismissed') === '1');
  if (dismissed) return null;

  const steps = [
    { done: activeCount > 0,  label: "Add your first grant",            action: () => { setActiveView("tracker"); setGosSubTab("active"); } },
    { done: appsLen > 0,      label: "Log a grant application",          action: () => { setActiveView("tracker"); setGosSubTab("applications"); } },
    { done: hasOrg,           label: "Complete your organisation profile", action: () => setActiveView("settings") },
    { done: false,            label: "Explore grant opportunities",       action: () => setActiveView("discover") },
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (doneCount === steps.length) return null;

  return (
    <div style={{ background: T.bgSurface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${TOKENS.gold}`, borderRadius: TOKENS.radiusMd, padding: "16px 20px", marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT_UI }}>Getting Started</div>
          <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT_UI, marginTop: 2 }}>{doneCount} of {steps.length} complete</div>
        </div>
        <button onClick={() => { localStorage.setItem('nofodesk_gs_dismissed', '1'); setDismissed(true); }} title="Dismiss" style={{ background: "transparent", border: "none", color: T.textGhost, fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "2px 4px" }}>×</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {steps.map((s, i) => (
          <div key={i} onClick={s.done ? undefined : s.action} style={{ display: "flex", alignItems: "center", gap: 10, cursor: s.done ? "default" : "pointer", opacity: s.done ? 0.5 : 1 }}>
            <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${s.done ? TOKENS.statusGreen : T.border}`, background: s.done ? TOKENS.statusGreen : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {s.done && <span style={{ color: "#fff", fontSize: 10, fontWeight: 700 }}>✓</span>}
            </div>
            <span style={{ fontSize: 12, fontFamily: FONT_UI, color: s.done ? T.textMuted : T.text, textDecoration: s.done ? "line-through" : "none" }}>{s.label}</span>
            {!s.done && <span style={{ fontSize: 10, color: T.gold, fontFamily: FONT_UI, marginLeft: "auto" }}>→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── DashboardView ─────────────────────────────────────────────
function DashboardView({ setActiveView, setGosSubTab, setGosPortFilter, setGosAppFilter }) {
  const T        = useT();
  const _vw      = useWindowWidth();
  const isMobile = _vw < 768;
  const gridCols = _vw >= 1024 ? "repeat(4, 1fr)" : _vw >= 600 ? "repeat(2, 1fr)" : "1fr";
  const [chartRange, setChartRange] = useState("all");
  const [pieMode,    setPieMode]    = useState("grants");

  /* ── Data ──────────────────────────────────────────── */
  const [activeGrants,     setDashActive]     = useState([]);
  const [apps,             setDashApps]       = useState([]);
  const [completedGrants,  setDashCompleted]  = useState([]);
  const [terminatedGrants, setDashTerminated] = useState([]);

  useEffect(() => {
    if (!getToken()) return;
    Promise.all([
      apiLoadTable('active-grants'),
      apiLoadTable('pipeline-apps'),
      apiLoadTable('terminated-grants'),
      apiLoadTable('completed-grants'),
    ]).then(([active, appsData, terminated, completed]) => {
      if (active)     setDashActive(active);
      if (appsData)   setDashApps(appsData);
      if (terminated) setDashTerminated(terminated);
      if (completed)  setDashCompleted(completed);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* KPIs — recompute only when data changes */
  const { portfolioValue, activeCount, expiringSoon, pendingCount, completedCount } = useMemo(() => {
    const now = new Date();
    const cap = new Date(now); cap.setDate(now.getDate() + 90);
    return {
      portfolioValue: activeGrants.reduce((s, g) => s + parseAmt(g.totalUSD), 0),
      activeCount:    activeGrants.length,
      expiringSoon:   activeGrants.filter(g => {
        const d = new Date(g.end || "");
        return !isNaN(d) && d >= now && d <= cap;
      }).length,
      pendingCount:   apps.filter(a => a.awardStatus === "Pending").length,
      completedCount: completedGrants.length,
    };
  }, [activeGrants, apps, completedGrants]);

  /* ── Chart + pie + bar data — recompute only when grants/apps change ── */
  const { allChartData, pieDataGrants, pieDataApps } = useMemo(() => {
    const yearFundingMap = {};

    const validYear = (y) => Number.isInteger(y) && y >= 2000 && y <= 2100;

    // Process any grant into the year funding map — works for active, completed, terminated
    const addGrantToChart = (g) => {
      const startField = g.start || g.startDate || "";
      const endField   = g.end   || g.endDate   || "";
      const totalField = g.totalUSD || g.awardedAmount || "";
      const s     = parseInt(startField.slice(0, 4));
      const e     = parseInt(endField.slice(0, 4));
      const total = parseAmt(totalField);
      const yearPeriod = g.year ? parseInt(String(g.year).slice(0, 4)) : null;
      if (!total) return;
      const fyAmounts = { ...(g.fyAmounts || {}) };
      if (g.fy26 && !fyAmounts[2026]) fyAmounts[2026] = g.fy26;
      const hasNOA = Object.values(fyAmounts).some(v => parseAmt(v) > 0);
      if (hasNOA) {
        Object.entries(fyAmounts).forEach(([yr, amt]) => {
          const y = parseInt(yr);
          const a = parseAmt(amt);
          if (validYear(y) && a > 0) yearFundingMap[y] = (yearFundingMap[y] || 0) + a;
        });
      } else if (validYear(s)) {
        const last   = (validYear(e) && e >= s) ? e : s;
        const annual = total / (last - s + 1);
        for (let yr = s; yr <= last; yr++) {
          yearFundingMap[yr] = (yearFundingMap[yr] || 0) + annual;
        }
      } else if (validYear(yearPeriod)) {
        yearFundingMap[yearPeriod] = (yearFundingMap[yearPeriod] || 0) + total;
      }
    };

    activeGrants.forEach(addGrantToChart);
    completedGrants.forEach(addGrantToChart);
    terminatedGrants.forEach(addGrantToChart);

    const allChartData = Object.entries(yearFundingMap)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([year, value]) => ({ year: Number(year), value }));

    const pieDataGrants = [
      { name: "Active",     value: activeGrants.length,      color: TOKENS.statusGreen },
      { name: "Completed",  value: completedGrants.length,   color: TOKENS.gold },
      { name: "Terminated", value: terminatedGrants.length,  color: TOKENS.statusRed },
    ].filter(d => d.value > 0);

    const pieDataApps = [
      { name: "Successful",        value: apps.filter(a => a.awardStatus === "Awarded").length,                                                            color: TOKENS.statusGreen },
      { name: "Unsuccessful",      value: apps.filter(a => a.awardStatus === "Not Awarded").length,                                                         color: TOKENS.statusRed },
      { name: "Ongoing",           value: apps.filter(a => a.awardStatus === "Ongoing Application" || a.status === "Ongoing").length,                       color: TOKENS.slateBlue },
      { name: "Awaiting Feedback", value: apps.filter(a => a.awardStatus === "Pending" || a.awardStatus === "Submitted").length,                            color: TOKENS.statusAmber },
    ].filter(d => d.value > 0);

    const appStatusMap = {};
    apps.forEach(a => {
      const s = a.awardStatus || "Unknown";
      appStatusMap[s] = (appStatusMap[s] || 0) + 1;
    });
    const barData = Object.entries(appStatusMap)
      .sort(([, a], [, b]) => b - a)
      .map(([status, count]) => ({ status, count }));

    return { allChartData, pieDataGrants, pieDataApps, barData };
  }, [activeGrants, apps, completedGrants, terminatedGrants]);

  const chartData = chartRange === "3y" ? allChartData.slice(-3)
    : chartRange === "5y" ? allChartData.slice(-5)
    : allChartData;

  const pieData = pieMode === "grants" ? pieDataGrants : pieDataApps;

  /* ── KPI definitions ──────────────────────────────────── */
  const kpis = [
    {
      label: "Active Portfolio Value",
      rawValue: portfolioValue,
      formatFn: v => v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v).toLocaleString()}`,
      subtitle: `Across ${activeCount} active grant${activeCount !== 1 ? "s" : ""}`,
      status: "green",
      onClick: () => { setGosPortFilter("all"); setActiveView("tracker"); setGosSubTab("active"); },
    },
    {
      label: "Expiring in 90 Days",
      rawValue: expiringSoon,
      formatFn: v => String(v),
      subtitle: "Requires renewal attention",
      status: expiringSoon === 0 ? "green" : expiringSoon <= 5 ? "amber" : "red",
      onClick: () => { setGosPortFilter("ending"); setActiveView("tracker"); setGosSubTab("active"); },
    },
    {
      label: "Applications Pending",
      rawValue: pendingCount,
      formatFn: v => String(v),
      subtitle: "Awaiting funder response",
      status: "blue",
      onClick: () => { setGosAppFilter("pending"); setActiveView("tracker"); setGosSubTab("applications"); },
    },
    {
      label: "Completed Grants",
      rawValue: completedCount,
      formatFn: v => String(v),
      subtitle: "Successfully closed",
      status: "green",
      onClick: () => { setActiveView("tracker"); setGosSubTab("completed"); },
    },
  ];

  const cardStyle = {
    background: T.bgSurface,
    border: `1px solid ${T.border}`,
    borderRadius: TOKENS.radiusMd,
    padding: isMobile ? "16px" : "22px 24px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
  };

  const cardTitleStyle = {
    fontSize: TOKENS.textLg,
    fontFamily: FONT_H,
    color: T.text,
    marginBottom: 3,
    lineHeight: 1.2,
  };

  const cardSubStyle = {
    fontSize: 11,
    color: T.textMuted,
    fontFamily: FONT_UI,
    marginBottom: 18,
  };

  /* ── Empty state ─────────────────────────────────────── */
  if (activeCount === 0 && apps.length === 0 && completedCount === 0) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", textAlign: "center", padding: "48px 24px", background: T.bgApp }}>
        <FileText size={48} color={T.textGhost} style={{ marginBottom: 16 }} />
        <div style={{ fontSize: TOKENS.textXl, fontFamily: FONT_H, color: T.textSub, marginBottom: 8, lineHeight: 1.3 }}>
          Your portfolio is empty
        </div>
        <div style={{ fontSize: TOKENS.textSm, fontFamily: FONT_UI, color: T.textMuted, marginBottom: 28, maxWidth: 380, lineHeight: 1.6 }}>
          Start by extracting your first grant or add one manually.
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button onClick={() => setActiveView("extract")} style={{ padding: "10px 22px", background: T.gold, border: "none", color: T.bgApp, borderRadius: TOKENS.radiusSm, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer" }}>
            Extract Grants
          </button>
          <button onClick={() => { setActiveView("tracker"); setGosSubTab("active"); }} style={{ padding: "10px 22px", background: "transparent", border: `1px solid ${T.border}`, color: T.textMuted, borderRadius: TOKENS.radiusSm, fontSize: 13, fontFamily: FONT_UI, cursor: "pointer" }}>
            Add Grant
          </button>
        </div>
      </div>
    );
  }

  /* ── Main render ──────────────────────────────────────── */
  return (
    <div style={{ background: T.bgApp, minHeight: "100%", padding: isMobile ? "16px 14px" : "28px 32px", fontFamily: FONT_UI }}>

      {/* Dashboard header + Export PPT button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: isMobile ? 22 : 28, fontFamily: FONT_H, color: T.text, fontWeight: 400, letterSpacing: "-0.02em" }}>Portfolio Dashboard</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT_UI, marginTop: 3 }}>Overview of your grant portfolio</div>
        </div>
        <GatedButton feature="export" T={T}>
          <button
            onClick={async () => {
              const orgProfile = loadOrgProfile();
              const STATUS_COLOR = { green: TOKENS.statusGreen, amber: TOKENS.statusAmber, red: TOKENS.statusRed, blue: TOKENS.statusBlue };
              const kpiData = kpis.map(k => ({
                label:       k.label,
                formatted:   k.formatFn(k.rawValue),
                accentColor: (STATUS_COLOR[k.status] || TOKENS.statusGreen).replace('#', ''),
              }));
              await exportDashboardPPT({
                kpis:          kpiData,
                chartData:     allChartData,
                pieDataGrants,
                pieDataApps,
                orgName:       orgProfile?.orgName || orgProfile?.name || 'Portfolio Report',
              });
            }}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 18px", borderRadius: 6,
              background: "transparent", border: `1px solid ${T.gold}`,
              color: T.gold, fontSize: 12, fontWeight: 600, fontFamily: FONT_UI,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 15 }}>⬇</span> Export PPT
          </button>
        </GatedButton>
      </div>

      <GettingStartedWidget activeCount={activeCount} appsLen={apps.length} setActiveView={setActiveView} setGosSubTab={setGosSubTab} T={T} />

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: isMobile ? 12 : 16, marginBottom: 24 }}>
        {kpis.map((k, i) => <DashKPICard key={i} {...k} isMobile={isMobile} />)}
      </div>

      {/* ── Charts row: Bar + Pie side by side ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 20 }}>

        {/* Bar chart — Portfolio Activity */}
        <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
          <div>
            <div style={cardTitleStyle}>Portfolio Activity</div>
            <div style={cardSubStyle}>Annual funding across all grants by fiscal year</div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {[["3y", "3 Years"], ["5y", "5 Years"], ["all", "All Time"]].map(([key, lbl]) => (
              <button key={key} onClick={() => setChartRange(key)} style={{
                padding: "5px 11px", fontSize: 11, fontFamily: FONT_UI, cursor: "pointer",
                borderRadius: TOKENS.radiusSm, transition: "all 0.12s",
                background: chartRange === key ? T.bgElevated : "transparent",
                border: `1px solid ${chartRange === key ? T.border : "transparent"}`,
                color: chartRange === key ? T.textSub : T.textMuted,
              }}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted, fontSize: 13 }}>
            No chart data available
          </div>
        ) : (
          <div style={{ filter: "drop-shadow(0 4px 16px rgba(201,162,39,0.12))" }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 0 }} barSize={Math.max(18, Math.min(48, 400 / chartData.length))}>
                <defs>
                  <linearGradient id="portfolioGoldBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={TOKENS.gold} stopOpacity={1} />
                    <stop offset="100%" stopColor={TOKENS.gold} stopOpacity={0.5} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={T.border} strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="year" tick={{ fill: T.textMuted, fontSize: 11, fontFamily: FONT_UI }} axisLine={false} tickLine={false} />
                <YAxis
                  tickFormatter={v => v >= 1e6 ? `$${(v / 1e6).toFixed(0)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`}
                  tick={{ fill: T.textMuted, fontSize: 11, fontFamily: FONT_UI }}
                  axisLine={false} tickLine={false} width={58}
                />
                <Tooltip content={<DashChartTooltip />} cursor={{ fill: "rgba(201,162,39,0.06)" }} />
                <Bar dataKey="value" fill="url(#portfolioGoldBar)" radius={[3, 3, 0, 0]} shape={<DashBar3D fill={TOKENS.gold} />} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

        {/* Pie chart — Portfolio Breakdown */}
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
            <div style={cardTitleStyle}>{pieMode === "grants" ? "Portfolio Breakdown" : "Applications Breakdown"}</div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              {[["grants", "Grants"], ["apps", "Applications"]].map(([key, lbl]) => (
                <button key={key} onClick={() => setPieMode(key)} style={{
                  padding: "4px 9px", fontSize: 10, fontFamily: FONT_UI, cursor: "pointer",
                  borderRadius: TOKENS.radiusSm, transition: "all 0.12s",
                  background: pieMode === key ? T.bgElevated : "transparent",
                  border: `1px solid ${pieMode === key ? T.border : "transparent"}`,
                  color: pieMode === key ? T.textSub : T.textMuted,
                }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div style={{ ...cardSubStyle, marginBottom: 14 }}>{pieMode === "grants" ? "Grants by lifecycle stage" : "Applications by award status"}</div>
          {pieData.length === 0 ? (
            <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted, fontSize: 13 }}>No data</div>
          ) : (
            <>
              <div style={{ filter: "drop-shadow(0 6px 20px rgba(0,0,0,0.5))" }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <defs>
                      {pieData.map((d, i) => (
                        <radialGradient key={i} id={`pie-grad-${i}`} cx="30%" cy="30%" r="70%">
                          <stop offset="0%"   stopColor={d.color} stopOpacity={1} />
                          <stop offset="100%" stopColor={d.color} stopOpacity={0.55} />
                        </radialGradient>
                      ))}
                    </defs>
                    <Pie
                      data={pieData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={88}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((d, i) => (
                        <Cell key={i} fill={`url(#pie-grad-${i})`} stroke={T.bgSurface} strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<DashPieTooltip pieData={pieData} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 4 }}>
                {pieData.map((d, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, flexShrink: 0, boxShadow: `0 0 6px ${d.color}60` }} />
                    <span style={{ fontSize: 12, color: T.textSub, fontFamily: FONT_UI, flex: 1 }}>{d.name}</span>
                    <span style={{ fontSize: 12, color: d.color, fontFamily: FONT_UI, fontVariantNumeric: "tabular-nums" }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default DashboardView;
